import { useEffect, useRef, useState } from 'react'
import Lenis from 'lenis'
import WelcomeCard from './WelcomeCard'
import TrustLockup from './TrustLockup'
import ThemeToggle from './ThemeToggle'

/* ============================================================
   ScrollWelcome — cinematic pre-form welcome for start.brite.live
   Beat 1  "This is what arrives"  — canvas frame-scrub (box opens),
                                     then in the last ~30% the open box
                                     recedes up + shrinks toward top-center,
                                     edges feathering into page white.
   Beat 2  "Let's get you set up"  — pinned final screen: receded box as a
                                     crown top-center, WelcomeCard in the
                                     middle column, empty L/R columns reserved
                                     for the bottle lineup + hero video later.
   Lite mode / reduced-motion never reach this component (App.jsx gates it).
   ============================================================ */

const FRAME_COUNT = 180
const framePath = (i) =>
  `/welcome/frames/arrive/frame_${String(i).padStart(4, '0')}.jpg`

const clamp01 = (v) => Math.min(Math.max(v, 0), 1)
// progress of `p` within [a,b]
const seg = (p, a, b) => clamp01((p - a) / (b - a))
const easeInOut = (t) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

// The settled studio grey, sampled from frame_0180's own background — the box
// sits on it, dissolves into it, and the welcome page uses it. One grey, no jump.
const STUDIO_GREY = '#cccec9'

// Theme-aware WASH color only. Beat 1's canvas stays white always (see draw());
// only the grey wash at the tail dissolves into the active theme — studio grey
// in light, #121212 in dark — so the welcome card + form pick up from there.
// Read live (cheap) so toggling the theme updates the wash on the next frame.
const getThemeColors = () => {
  const dark =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'dark'
  return dark
    ? { wash: '#121212', washRGB: '18, 18, 18' }
    : { wash: STUDIO_GREY, washRGB: '204, 206, 201' }
}

// Beat 1 budget: frames scrub over the first SCRUB_END of the section, the
// grey wash plays across the remaining tail, and the welcome overlay resolves
// in place once the wash has covered the box (WELCOME_IN onward).
const SCRUB_END = 0.7
const WELCOME_IN = 0.65

export default function ScrollWelcome({
  onBegin,
  hasSavedState,
}) {
  const canvasRef = useRef(null)
  const arriveRef = useRef(null)
  const brandRef = useRef(null)
  const hintRef = useRef(null)
  const veilRef = useRef(null)
  const welcomeLayerRef = useRef(null)
  const lenisRef = useRef(null)
  const [ready, setReady] = useState(false)

  // Smoothly play the whole cinematic through to the welcome resting point
  // (p = 1.0 — frames done, grey wash done, welcome fully resolved). Recompute
  // the target at call time: a resize changes the arrive section's height.
  // Reuses the module-level easeInOut for the scrollTo easing.
  const scrollToWelcome = (duration = 1.4) => {
    const lenis = lenisRef.current
    const sec = arriveRef.current
    if (!lenis || !sec) return
    const rect = sec.getBoundingClientRect()
    const sectionTop = window.scrollY + rect.top
    const targetY = sectionTop + (rect.height - window.innerHeight)
    lenis.scrollTo(targetY, { duration, easing: easeInOut })
  }

  // Click anywhere on the hero BEFORE the welcome resolves → play it through.
  // Once the welcome layer is interactive (.is-in), let the card's own tap
  // (handleCardTap → onBegin) run instead; this no-ops there.
  const handleHeroClick = () => {
    if (welcomeLayerRef.current?.classList.contains('is-in')) return
    scrollToWelcome()
  }

  // The app shell locks html/body/#root to a fixed viewport (overflow:hidden)
  // for the classic welcome + form. Unlock document scrolling only while this
  // component is mounted; cleanup restores the locked shell for the form.
  useEffect(() => {
    document.documentElement.classList.add('sw-scroll-on')
    return () => document.documentElement.classList.remove('sw-scroll-on')
  }, [])

  // ── frame preload + canvas scrub + lenis loop ──
  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d', { alpha: false })
    const images = []
    let firstDrawn = false
    let current = -1
    let loaded = 0
    // Snap-through guard: fire the auto-complete scroll ONCE per entry into the
    // cinematic; reset only when the user returns near the very top.
    let snapInFlight = false

    for (let i = 0; i < FRAME_COUNT; i++) {
      const img = new Image()
      img.src = framePath(i + 1)
      img.onload = () => {
        loaded++
        if (!firstDrawn && i === 0) {
          firstDrawn = true
          draw(0)
          setReady(true)
        }
        // safety: if frame 1 errored/cached oddly, mark ready once most load
        if (!firstDrawn && loaded > FRAME_COUNT * 0.5) {
          firstDrawn = true
          draw(0)
          setReady(true)
        }
      }
      images[i] = img
    }

    function draw(index) {
      const img = images[index]
      if (!img || !img.complete || !img.naturalWidth) return
      const cw = canvas.clientWidth
      const ch = canvas.clientHeight
      const ir = img.naturalWidth / img.naturalHeight
      const cr = cw / ch
      let dw, dh, dx, dy
      if (ir > cr) {
        dh = ch
        dw = ch * ir
        dx = (cw - dw) / 2
        dy = 0
      } else {
        dw = cw
        dh = cw / ir
        dx = 0
        dy = (ch - dh) / 2
      }
      // Beat 1 is ALWAYS light — the box-opening scrub is white-box product
      // photography and must never go dark, regardless of theme. Only the wash
      // (applyRecede) dissolves into the active theme once Beat 1 ends.
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, cw, ch)
      ctx.drawImage(img, dx, dy, dw, dh)
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = canvas.clientWidth * dpr
      canvas.height = canvas.clientHeight * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      draw(current < 0 ? 0 : current)
    }

    const setLine = (el, o, y = 30) => {
      if (!el) return
      el.style.opacity = o.toFixed(3)
      el.style.transform = `translateY(${(1 - o) * y}px)`
    }

    // The grey wash — Beat 1 ends on the full-frame open box (frame_0180), which
    // stays put (no move/scale). As the tail scrolls, a veil of the exact studio
    // grey rises from the bottom and blooms up over the box until the screen is
    // flat grey — the same grey the welcome page uses, so Beat 2 is seamless.
    // It's a soft luminance bloom, not a hard crossfade: the grey climbs with a
    // wide feather so the box softens into it, its top going last. Nothing pops.
    // `t` is the raw 0→1 progress across the recede tail.
    function applyRecede(t) {
      const veil = veilRef.current
      if (!veil) return
      // box never transforms now — clear any stale transform from older builds
      if (canvas.style.transform && canvas.style.transform !== 'none') {
        canvas.style.transform = 'none'
      }
      if (t <= 0) {
        veil.style.opacity = '0'
        return
      }
      // wash completes a touch before the welcome appears, so the welcome
      // resolves on fully-settled grey (one screen, no half-washed overlap).
      const e = easeInOut(clamp01(t / 0.62))
      veil.style.opacity = '1'
      // grey rises from the bottom: solid up to (cov - feather), softly feathering
      // to transparent at cov. cov climbs past 100% so the top fully greys by t=1.
      const cov = e * 150
      const feather = 34
      const solid = Math.max(cov - feather, 0)
      const { wash, washRGB } = getThemeColors()
      veil.style.background =
        `linear-gradient(to top, ${wash} 0%, ${wash} ${solid.toFixed(1)}%, rgba(${washRGB},0) ${cov.toFixed(1)}%)`
    }

    function update() {
      const vh = window.innerHeight

      /* ── Beat 1: box-open scrub, then recede ── */
      const a = arriveRef.current
      if (a) {
        const rect = a.getBoundingClientRect()
        if (!(rect.bottom < -vh || rect.top > vh)) {
          const p = clamp01(-rect.top / (rect.height - vh))
          // Snap-through: once the user nudges into the cinematic (p past a tiny
          // threshold) heading DOWN, auto-complete the scroll to the welcome
          // resting point so they can never park on the empty grey-wash tail.
          // Fires once per entry; resets near the very top; never on upward
          // intent; never once the welcome is resolved (.is-in).
          const resolved = welcomeLayerRef.current?.classList.contains('is-in')
          if (p <= 0.005) {
            snapInFlight = false
          } else if (!snapInFlight && !resolved && p > 0.01 && lenis.direction === 1) {
            snapInFlight = true
            scrollToWelcome(1.2)
          }
          // frames complete by SCRUB_END, holding the open-box pose
          const sp = seg(p, 0, SCRUB_END)
          const idx = Math.min(
            FRAME_COUNT - 1,
            Math.floor(sp * (FRAME_COUNT - 1)),
          )
          if (idx !== current) {
            current = idx
            draw(idx)
          }
          // the one brand line: lives in the opening negative space, fades
          // out as the lid lifts and the box fills the frame
          setLine(brandRef.current, clamp01(1 - seg(p, 0.08, 0.2)), 14)
          if (hintRef.current)
            hintRef.current.style.opacity = p > 0.04 ? '0' : '1'
          // the recede (grey wash) plays across the tail; the same progress
          // fades the welcome overlay in once the grey has covered the box —
          // all on this one sticky screen.
          const t = seg(p, SCRUB_END, 1.0)
          applyRecede(t)
          const layer = welcomeLayerRef.current
          if (layer) layer.classList.toggle('is-in', t >= WELCOME_IN)
        }
      }
    }

    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true })
    lenisRef.current = lenis
    let rafId
    const raf = (t) => {
      lenis.raf(t)
      update()
      rafId = requestAnimationFrame(raf)
    }
    rafId = requestAnimationFrame(raf)

    window.addEventListener('resize', resize)
    resize()
    update()

    return () => {
      cancelAnimationFrame(rafId)
      lenis.destroy()
      lenisRef.current = null
      window.removeEventListener('resize', resize)
    }
  }, [])

  // Enter/Space anywhere begins the form (matches WelcomePage behavior)
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement
      return (
        !!el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.isContentEditable)
      )
    }
    const onKey = (e) => {
      if (isTyping()) return
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault()
        onBegin()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onBegin])


  const handleCardTap = (e) => {
    const t = e.target
    if (t.closest?.('[data-no-tap]') || t.closest?.('a, input, textarea, select'))
      return
    onBegin()
  }

  return (
    <div className={`sw-root ${ready ? 'sw-ready' : ''}`}>
      {/* ── ONE pinned stage: box opens (scrub) → grey wash → welcome resolves
           in place on the same sticky screen. No second section. ── */}
      <section ref={arriveRef} className="sw-arrive" id="sw-arrive">
        <div className="sw-sticky" onClick={handleHeroClick} style={{ cursor: 'pointer' }}>
          <canvas ref={canvasRef} className="sw-canvas" />
          {/* grey wash veil — rises over the box at the tail (see applyRecede) */}
          <div ref={veilRef} className="sw-veil" aria-hidden="true" />
          <p ref={brandRef} className="sw-brand">
            Welcome to BRITE
          </p>
          <div ref={hintRef} className="sw-hint" aria-hidden="true">
            <span className="sw-hint-text">Scroll</span>
            <span className="sw-hint-arrow">↓</span>
          </div>

          {/* welcome overlay — fades in on THIS screen once the wash covers the
              box (update() toggles .is-in at WELCOME_IN); then it's interactive */}
          <div
            ref={welcomeLayerRef}
            className="sw-welcome-layer"
            onClick={handleCardTap}
            role="presentation"
          >
            {/* theme toggle — lives inside the welcome layer so it's hidden
                during Beat 1 (the scroll cinematic) and only appears once the
                welcome card resolves (.is-in), preventing mid-scroll toggling */}
            <ThemeToggle className="sw-theme-toggle" />

            {/* brand anchor — full teal icon + BRITE wordmark lockup, top-left */}
            <img
              className="sw-brandmark"
              src="/welcome/brite-lockup-dark.png"
              alt="BRITE"
            />

            {/* RIGHT — brand as material: huge blind-debossed spiral in the grey */}
            <div className="sw-emboss" aria-hidden="true" />

            {/* LEFT — the welcome, left-anchored, staged reveal */}
            <div className="sw-final-body">
              <div
                className="sw-welcome"
                data-no-tap={hasSavedState ? 'true' : undefined}
              >
                <div className="sw-welcome-rule" aria-hidden="true" />
                <WelcomeCard onBegin={onBegin} />
              </div>
            </div>

            <footer className="bottom-bar">
              <div className="bb-left" aria-hidden="true" />
              <div className="bb-center">
                <TrustLockup />
              </div>
              <div className="bb-right" aria-hidden="true" />
            </footer>
          </div>
        </div>
      </section>
    </div>
  )
}
